import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, type CreatorApplication } from '../../lib/supabase';
import { format } from 'date-fns';
import AdminLayout from '../../components/feature/AdminLayout';

export default function AdminApplications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<CreatorApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<CreatorApplication | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  const ADMIN_EMAILS = ['yirun0206@gmail.com'];

  // ---------------------------------------------------------------------------
  // Load applications – runs once the user is verified as an admin
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!user) {
      navigate('/auth/login');
      return;
    }
    if (!ADMIN_EMAILS.includes(user.email || '')) {
      navigate('/');
      return;
    }
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const loadApplications = async () => {
    try {
      const { data, error } = await supabase
        .from('creator_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApplications(data ?? []);
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Update status (approved / rejected)
  // ---------------------------------------------------------------------------
  const updateApplicationStatus = async (
    id: string,
    status: 'approved' | 'rejected'
  ) => {
    setUpdating(id);
    try {
      const { error } = await supabase
        .from('creator_applications')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      // Refresh list and close modal
      await loadApplications();
      setSelectedApp(null);
    } catch (error) {
      console.error('Error updating application:', error);
    } finally {
      setUpdating(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const filtered =
    filter === 'all' ? applications : applications.filter((a) => a.status === filter);

  const stats = {
    total: applications.length,
    pending: applications.filter((a) => a.status === 'pending').length,
    approved: applications.filter((a) => a.status === 'approved').length,
    rejected: applications.filter((a) => a.status === 'rejected').length,
  };

  const getStatusStyle = (status: string) => {
    if (status === 'pending') return 'bg-amber-500/20 text-amber-300';
    if (status === 'approved') return 'bg-teal-500/20 text-teal-300';
    return 'bg-red-500/20 text-red-300';
  };

  const getStatusBorderStyle = (status: string) => {
    if (status === 'pending')
      return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
    if (status === 'approved')
      return 'bg-teal-500/20 text-teal-300 border-teal-500/30';
    return 'bg-red-500/20 text-red-300 border-red-500/30';
  };

  const getStatusText = (status: string) => {
    if (status === 'pending') return '待審核';
    if (status === 'approved') return '已通過';
    return '已拒絕';
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <AdminLayout title="申請管理">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="申請管理">
      {/* ===== Desktop view ===== */}
      <div className="hidden md:block max-w-7xl mx-auto px-6 py-12">
        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <StatCard
            icon="ri-file-list-3-line"
            bg="bg-teal-500/20"
            title="總申請數"
            value={stats.total}
          />
          <StatCard
            icon="ri-time-line"
            bg="bg-yellow-500/20"
            title="待審核"
            value={stats.pending}
          />
          <StatCard
            icon="ri-checkbox-circle-line"
            bg="bg-teal-500/20"
            title="已通過"
            value={stats.approved}
          />
          <StatCard
            icon="ri-close-circle-line"
            bg="bg-red-500/20"
            title="已拒絕"
            value={stats.rejected}
          />
        </div>

        {/* Table */}
        <div className="bg-slate-800/80 rounded-xl border border-slate-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-xl font-bold text-white">創作者申請列表</h2>
          </div>
          {applications.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                      姓名
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                      聯絡方式
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                      遊戲類型
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                      狀態
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                      申請日期
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {applications.map((app) => (
                    <tr
                      key={app.id}
                      className="hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <span className="text-white font-medium">{app.name}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-300 text-sm">{app.contact}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-300 text-sm">{app.game_type}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBorderStyle(
                            app.status
                          )}`}
                        >
                          {getStatusText(app.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-400 text-sm">
                          {format(new Date(app.created_at), 'yyyy/MM/dd HH:mm')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedApp(app)}
                            className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                          >
                            查看詳情
                          </button>
                          {app.status === 'pending' && (
                            <>
                              <button
                                onClick={() => updateApplicationStatus(app.id, 'approved')}
                                disabled={updating === app.id}
                                className="px-3 py-1 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-600 text-white rounded text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
                              >
                                通過
                              </button>
                              <button
                                onClick={() => updateApplicationStatus(app.id, 'rejected')}
                                disabled={updating === app.id}
                                className="px-3 py-1 bg-red-500 hover:bg-red-600 disabled:bg-slate-600 text-white rounded text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
                              >
                                拒絕
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ===== Mobile view ===== */}
      <div className="md:hidden overflow-x-hidden">
        <MobileTabs stats={stats} filter={filter} setFilter={setFilter} />
        <div className="px-4 pt-2">
          {filtered.length === 0 ? (
            <EmptyStateMobile />
          ) : (
            <div className="space-y-2">
              {filtered.map((app) => (
                <button
                  key={app.id}
                  onClick={() => setSelectedApp(app)}
                  className="w-full text-left bg-slate-900 rounded-xl px-4 py-3 border border-slate-800/60 active:bg-slate-800 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <div className="w-8 h-8 flex items-center justify-center bg-slate-800 rounded-full flex-shrink-0">
                        <i className="ri-user-line text-sm text-slate-400"></i>
                      </div>
                      <span className="text-white text-sm font-medium truncate">
                        {app.name}
                      </span>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ml-2 ${getStatusStyle(
                        app.status
                      )}`}
                    >
                      {getStatusText(app.status)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-slate-500">
                    <span className="truncate flex-1">{app.game_type}</span>
                    <span className="flex-shrink-0">
                      {format(new Date(app.created_at), 'MM/dd HH:mm')}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ===== Detail modal / fullscreen ===== */}
      {selectedApp && (
        <>
          {/* Desktop modal */}
          <div
            className="hidden md:flex fixed inset-0 bg-black/70 items-center justify-center z-50 p-6"
            onClick={() => setSelectedApp(null)}
          >
            <div
              className="bg-slate-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-slate-700"
              onClick={(e) => e.stopPropagation()}
            >
              <DetailHeader onClose={() => setSelectedApp(null)} />
              <DetailBody selectedApp={selectedApp} getStatusBorderStyle={getStatusBorderStyle} getStatusText={getStatusText} />
              {selectedApp.status === 'pending' && (
                <DetailActions
                  selectedApp={selectedApp}
                  updating={updating}
                  updateApplicationStatus={updateApplicationStatus}
                />
              )}
            </div>
          </div>

          {/* Mobile fullscreen */}
          <div className="md:hidden fixed inset-0 z-50 bg-slate-950 flex flex-col animate-slideUp overflow-x-hidden">
            <MobileHeader selectedApp={selectedApp} onClose={() => setSelectedApp(null)} />
            <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4">
              <MobileInfo selectedApp={selectedApp} />
            </div>
            {selectedApp.status === 'pending' && (
              <MobileActions
                selectedApp={selectedApp}
                updating={updating}
                updateApplicationStatus={updateApplicationStatus}
              />
            )}
          </div>
        </>
      )}

      {/* Inline CSS – extracted to a constant to avoid JSX parsing issues */}
      <style>{styleContent}</style>
    </AdminLayout>
  );
}

/* ---------------------------------------------------------------------------
   Helper components – kept minimal for readability
   --------------------------------------------------------------------------- */
function StatCard({
  icon,
  bg,
  title,
  value,
}: {
  icon: string;
  bg: string;
  title: string;
  value: number;
}) {
  return (
    <div className="bg-slate-800/80 rounded-xl p-6 border border-slate-700">
      <div className={`w-12 h-12 flex items-center justify-center ${bg} rounded-lg mb-4`}>
        <i className={`${icon} text-2xl text-white`}></i>
      </div>
      <p className="text-slate-400 text-sm mb-1">{title}</p>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="p-12 text-center">
      <i className="ri-inbox-line text-6xl text-slate-600 mb-4 block"></i>
      <p className="text-slate-400">暫無申請資料</p>
    </div>
  );
}

function EmptyStateMobile() {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="w-14 h-14 flex items-center justify-center bg-slate-800 rounded-full mb-3">
        <i className="ri-inbox-line text-2xl text-slate-600"></i>
      </div>
      <p className="text-slate-500 text-xs">暫無申請資料</p>
    </div>
  );
}

function MobileTabs({
  stats,
  filter,
  setFilter,
}: {
  stats: { total: number; pending: number; approved: number; rejected: number };
  filter: string;
  setFilter: (v: any) => void;
}) {
  const tabs = [
    { key: 'all' as const, label: '全部', count: stats.total },
    { key: 'pending' as const, label: '待審核', count: stats.pending },
    { key: 'approved' as const, label: '已通過', count: stats.approved },
    { key: 'rejected' as const, label: '已拒絕', count: stats.rejected },
  ];
  return (
    <div className="px-4 pt-3 pb-2 sticky top-[52px] z-30 bg-slate-950 overflow-x-hidden">
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 ${
              filter === tab.key
                ? 'bg-teal-500 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-slate-300'
            }`}
          >
            {tab.label}
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                filter === tab.key ? 'bg-white/20' : 'bg-slate-700'
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function DetailHeader({ onClose }: { onClose: () => void }) {
  return (
    <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-8 py-6 flex items-center justify-between">
      <h2 className="text-2xl font-bold text-white">申請詳情</h2>
      <button
        onClick={onClose}
        className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-slate-700 transition-colors cursor-pointer"
      >
        <i className="ri-close-line text-2xl text-slate-400"></i>
      </button>
    </div>
  );
}

function DetailBody({
  selectedApp,
  getStatusBorderStyle,
  getStatusText,
}: {
  selectedApp: CreatorApplication;
  getStatusBorderStyle: (s: string) => string;
  getStatusText: (s: string) => string;
}) {
  return (
    <div className="px-8 py-8 space-y-6">
      <InfoRow label="姓名或暱稱" value={selectedApp.name} />
      <InfoRow label="電子郵件" value={selectedApp.contact} />
      <InfoRow label="電話號碼" value={selectedApp.phone || '未提供'} />
      <InfoRow label="LINE ID" value={selectedApp.line_id || '未提供'} />
      {selectedApp.social_links && (
        <div>
          <label className="block text-slate-400 text-sm mb-2">社群平台連結／參考作品</label>
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
            <p className="text-slate-300 whitespace-pre-wrap">{selectedApp.social_links}</p>
          </div>
        </div>
      )}
      <InfoRow label="熟悉的遊戲類型" value={selectedApp.game_type} />
      {selectedApp.experience && (
        <div>
          <label className="block text-slate-400 text-sm mb-2">創作經驗簡述</label>
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
            <p className="text-slate-300 whitespace-pre-wrap">{selectedApp.experience}</p>
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-slate-400 text-sm mb-2">申請狀態</label>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBorderStyle(
              selectedApp.status
            )}`}
          >
            {getStatusText(selectedApp.status)}
          </span>
        </div>
        <div>
          <label className="block text-slate-400 text-sm mb-2">申請日期</label>
          <p className="text-white">
            {format(new Date(selectedApp.created_at), 'yyyy/MM/dd HH:mm')}
          </p>
        </div>
      </div>
    </div>
  );
}

function DetailActions({
  selectedApp,
  updating,
  updateApplicationStatus,
}: {
  selectedApp: CreatorApplication;
  updating: string | null;
  updateApplicationStatus: (id: string, status: 'approved' | 'rejected') => void;
}) {
  return (
    <div className="flex gap-4 pt-6 border-t border-slate-700">
      <button
        onClick={() => updateApplicationStatus(selectedApp.id, 'approved')}
        disabled={updating === selectedApp.id}
        className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-600 text-white py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
      >
        <i className="ri-checkbox-circle-line mr-2"></i>通過申請
      </button>
      <button
        onClick={() => updateApplicationStatus(selectedApp.id, 'rejected')}
        disabled={updating === selectedApp.id}
        className="flex-1 bg-red-500 hover:bg-red-600 disabled:bg-slate-600 text-white py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
      >
        <i className="ri-close-circle-line mr-2"></i>拒絕申請
      </button>
    </div>
  );
}

function MobileHeader({
  selectedApp,
  onClose,
}: {
  selectedApp: CreatorApplication;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800/60">
      <button
        onClick={onClose}
        className="flex items-center gap-1 text-teal-400 text-sm cursor-pointer whitespace-nowrap"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>返回
      </button>
      <span className="text-white text-sm font-semibold">申請詳情</span>
      <div className="w-14"></div>
    </div>
  );
}

function MobileInfo({ selectedApp }: { selectedApp: CreatorApplication }) {
  const getStatusStyle = (status: string) => {
    if (status === 'pending') return 'bg-amber-500/20 text-amber-300';
    if (status === 'approved') return 'bg-teal-500/20 text-teal-300';
    return 'bg-red-500/20 text-red-300';
  };

  const getStatusText = (status: string) => {
    if (status === 'pending') return '待審核';
    if (status === 'approved') return '已通過';
    return '已拒絕';
  };

  return (
    <div className="overflow-x-hidden">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 flex items-center justify-center bg-slate-800 rounded-full flex-shrink-0">
          <i className="ri-user-line text-xl text-slate-400"></i>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-white font-semibold text-base truncate">{selectedApp.name}</h2>
          <span
            className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium mt-0.5 ${getStatusStyle(
              selectedApp.status
            )}`}
          >
            {getStatusText(selectedApp.status)}
          </span>
        </div>
      </div>
      <div className="space-y-3">
        <div className="bg-slate-900 rounded-xl border border-slate-800/60 divide-y divide-slate-800/60 overflow-hidden">
          <MobileInfoRow icon="ri-mail-line" label="電子郵件" value={selectedApp.contact} />
          <MobileInfoRow icon="ri-phone-line" label="電話號碼" value={selectedApp.phone || '未提供'} />
          <MobileInfoRow icon="ri-chat-1-line" label="LINE ID" value={selectedApp.line_id || '未提供'} />
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800/60 divide-y divide-slate-800/60 overflow-hidden">
          <MobileInfoRow icon="ri-gamepad-line" label="遊戲類型" value={selectedApp.game_type} />
          <MobileInfoRow
            icon="ri-calendar-line"
            label="申請日期"
            value={format(new Date(selectedApp.created_at), 'yyyy/MM/dd HH:mm')}
          />
        </div>
        {selectedApp.social_links && (
          <div className="bg-slate-900 rounded-xl border border-slate-800/60 p-3.5 overflow-hidden">
            <p className="text-slate-500 text-[11px] mb-1.5">社群平台／參考作品</p>
            <p className="text-slate-300 text-xs whitespace-pre-wrap leading-relaxed break-words">
              {selectedApp.social_links}
            </p>
          </div>
        )}
        {selectedApp.experience && (
          <div className="bg-slate-900 rounded-xl border border-slate-800/60 p-3.5 overflow-hidden">
            <p className="text-slate-500 text-[11px] mb-1.5">創作經驗簡述</p>
            <p className="text-slate-300 text-xs whitespace-pre-wrap leading-relaxed break-words">
              {selectedApp.experience}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function MobileActions({
  selectedApp,
  updating,
  updateApplicationStatus,
}: {
  selectedApp: CreatorApplication;
  updating: string | null;
  updateApplicationStatus: (id: string, status: 'approved' | 'rejected') => void;
}) {
  return (
    <div className="px-4 py-3 border-t border-slate-800/60 flex gap-3 pb-[calc(12px+env(safe-area-inset-bottom))]">
      <button
        onClick={() => updateApplicationStatus(selectedApp.id, 'rejected')}
        disabled={updating === selectedApp.id}
        className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-red-400 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
      >
        拒絕
      </button>
      <button
        onClick={() => updateApplicationStatus(selectedApp.id, 'approved')}
        disabled={updating === selectedApp.id}
        className="flex-1 py-2.5 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
      >
        通過
      </button>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Simple reusable info row for both desktop and mobile details
   --------------------------------------------------------------------------- */
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="block text-slate-400 text-sm mb-2">{label}</label>
      <p className="text-white">{value}</p>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   MobileInfoRow component (unchanged)
   --------------------------------------------------------------------------- */
function MobileInfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-3.5 py-3 overflow-hidden">
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={`${icon} text-sm text-slate-500`}></i>
        </div>
        <span className="text-slate-500 text-xs whitespace-nowrap">{label}</span>
      </div>
      <span className="text-white text-xs font-medium text-right max-w-[60%] truncate">{value}</span>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Inline CSS moved to a constant string – avoids JSX parsing errors.
   --------------------------------------------------------------------------- */
const styleContent = `
  .no-scrollbar::-webkit-scrollbar { display: none; }
  .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
  .animate-slideUp { animation: slideUp 0.25s ease-out; }
`;

